// A counting semaphore that outlives a single batch. Upload jobs queued by
// separate clicks share one instance, so the in-flight cap holds across the
// whole session instead of resetting per batch.
export const createSemaphore = (limit) => {
  const maxActive = Math.max(1, limit)
  const waiting = []
  let active = 0

  const release = () => {
    const next = waiting.shift()
    if (next) {
      next.start()
      return
    }
    active -= 1
  }

  return {
    // `cost` orders the waiting list ascending, so a free slot goes to the
    // cheapest waiter rather than whoever asked first. Callers pass file size:
    // a small document then never trails a large one it happened to queue
    // behind. Equal costs keep their arrival order.
    acquire: (cost = 0) =>
      new Promise((resolve) => {
        if (active < maxActive) {
          active += 1
          resolve(release)
          return
        }
        const index = waiting.findIndex((queued) => queued.cost > cost)
        const waiter = { cost, start: () => resolve(release) }
        if (index === -1) waiting.push(waiter)
        else waiting.splice(index, 0, waiter)
      }),
  }
}

// Runs `worker` over `items` with at most `limit` in flight. On the first
// rejection it stops pulling new work but still lets the in-flight calls
// settle, so a worker that records progress as a side effect (a chunk etag,
// say) keeps what it already earned. The first error is rethrown afterwards.
export const runWithConcurrency = async (items, limit, worker) => {
  let firstError
  let nextIndex = 0

  const runWorker = async () => {
    while (nextIndex < items.length && !firstError) {
      const itemIndex = nextIndex
      nextIndex += 1
      try {
        await worker(items[itemIndex], itemIndex)
      } catch (error) {
        firstError = firstError || error
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(Math.max(1, limit), items.length) },
      runWorker,
    ),
  )

  if (firstError) throw firstError
}
